import type { SetSeasonTaxRateInput, UpsertBoatIncomeSettingsInput } from '@sea-snag/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { incomeApi } from '../lib/api-client';
import { queryKeys } from '../lib/query-keys';

/** A season's income per boat, currency and month (#217). */
export function useIncomeOverview(orgPid: string, year: number) {
  return useQuery({
    queryKey: queryKeys.analytics.income(orgPid, year),
    queryFn: () => incomeApi.get(year),
    enabled: !!orgPid,
  });
}

/**
 * Saves and locks one season's tax rate. Other seasons keep theirs; the page
 * recalculates once the save has gone through.
 */
export function useSetSeasonTaxRate(orgPid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: SetSeasonTaxRateInput) => incomeApi.setTaxRate(data),
    onSuccess: (result) => {
      toast.success(`Tax rate for ${result.year} saved and locked at ${result.ratePct}%`);
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.incomeAll(orgPid) });
    },
    onError: () => toast.error('Could not save the tax rate'),
  });
}

/** Saves a boat's ownership and owner share for one season. */
export function useSetBoatIncomeSettings(orgPid: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: { boatPid: string; data: UpsertBoatIncomeSettingsInput }) =>
      incomeApi.setBoatSettings(args.boatPid, args.data),
    onSuccess: (result) => {
      toast.success(`Saved for ${result.year}`);
      void qc.invalidateQueries({ queryKey: queryKeys.analytics.incomeAll(orgPid) });
    },
    onError: () => toast.error('Could not save the boat’s settings'),
  });
}
